<!-- Quality_Patients_List.vue -->
<template>
  <div class="flex flex-col">
    <!-- Filter Bar -->
    <Worklist_FilterBar
      ref="entityFilterBar"
      :initial-filters="entityInitialFilters"
      :hide-clear="true"
      :patient-search="true"
      :show-my-practices="myPracticesEntityType !== null"
      :my-practices-label="myPracticesEntityType === 'Contract' ? 'My Contracts' : 'My Practices'"
      @filter-changed="onFilterChanged"
    >
      <template #filters>
        <button
          @click="campaignModalOpen = true"
          :class="[
            'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
            selectedCampaignId
              ? 'bg-blue-dark text-white border-blue-dark'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
          ]"
        >
          <FontAwesomeIcon :icon="['fas', 'bullseye']" class="mr-1" />
          <span v-if="selectedCampaignId" class="sensitive">{{ selectedCampaignName }}</span>
          <span v-else>Campaign</span>
          <FontAwesomeIcon
            v-if="selectedCampaignId"
            :icon="['fas', 'xmark']"
            class="ml-2 text-[10px]"
            @click.stop="clearCampaign"
          />
        </button>

        <!-- Routing Tier Filter -->
        <div class="relative" ref="routingDropdown">
          <button
            @click="routingDropdownOpen = !routingDropdownOpen"
            :class="[
              'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
              selectedRoutingTier
                ? 'bg-amber-600 text-white border-amber-600'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            ]"
          >
            <FontAwesomeIcon :icon="['fas', 'route']" class="mr-1" />
            {{ selectedRoutingTier ? routingTierLabel : 'Next Step' }}
            <FontAwesomeIcon :icon="['fas', 'chevron-down']" class="ml-1 text-[10px]" />
          </button>
          <div
            v-if="routingDropdownOpen"
            class="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
          >
            <button
              @click="selectRoutingTier(null)"
              :class="[
                'w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center justify-between',
                !selectedRoutingTier ? 'font-semibold text-gray-900' : 'text-gray-600'
              ]"
            >
              <span>All Steps</span>
            </button>
            <button
              v-for="tier in routingTiers"
              :key="tier.value"
              @click="selectRoutingTier(tier.value)"
              :class="[
                'w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center justify-between border-t border-gray-100',
                selectedRoutingTier === tier.value ? 'font-semibold text-amber-700 bg-amber-50' : 'text-gray-600'
              ]"
            >
              <span>{{ tier.label }}</span>
              <span class="text-gray-400 text-[10px]" v-if="tier.count !== undefined">{{ tier.count.toLocaleString() }}</span>
            </button>
          </div>
        </div>

        <!-- Stage Filter -->
        <div class="relative" ref="stageDropdown" v-if="stageOptions.length">
          <button
            @click="stageDropdownOpen = !stageDropdownOpen"
            :class="[
              'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
              selectedStage
                ? 'bg-amber-600 text-white border-amber-600'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            ]"
          >
            <FontAwesomeIcon :icon="['fas', 'layer-group']" class="mr-1" />
            {{ selectedStage ? stageLabel : 'Stage' }}
            <FontAwesomeIcon :icon="['fas', 'chevron-down']" class="ml-1 text-[10px]" />
          </button>
          <div
            v-if="stageDropdownOpen"
            class="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
          >
            <button
              @click="selectStage(null)"
              :class="[
                'w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center justify-between',
                !selectedStage ? 'font-semibold text-gray-900' : 'text-gray-600'
              ]"
            >
              <span>All Stages</span>
            </button>
            <button
              v-for="stage in stageOptions"
              :key="stage.id"
              @click="selectStage(stage.id)"
              :class="[
                'w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center justify-between border-t border-gray-100',
                selectedStage === stage.id ? 'font-semibold text-amber-700 bg-amber-50' : 'text-gray-600'
              ]"
            >
              <span>{{ stage.name }}</span>
            </button>
          </div>
        </div>

        <button
          @click="toggleEvidenceFilter"
          :class="[
            'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
            evidenceFilterActive
              ? 'bg-green-600 text-white border-green-600'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
          ]"
        >
          <FontAwesomeIcon :icon="['fas', 'flask-vial']" class="mr-1" />
          Has EHR Evidence
        </button>
        <button
          @click="toggleClosureFilter"
          :class="[
            'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
            closureFilterActive
              ? 'bg-amber-600 text-white border-amber-600'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
          ]"
        >
          <FontAwesomeIcon :icon="['fas', 'check-circle']" class="mr-1" />
          Potentially Closed
        </button>
        <button
          @click="toggleFavoritesFilter"
          :class="[
            'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
            favoritesFilterActive
              ? 'bg-yellow-500 text-white border-yellow-500'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
          ]"
        >
          <FontAwesomeIcon :icon="['fas', 'star']" class="mr-1" />
          My Favorites
        </button>
        <button
          @click="toggleShowSnoozed"
          :class="[
            'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
            showSnoozed
              ? 'bg-yellow-600 text-white border-yellow-600'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
          ]"
        >
          <FontAwesomeIcon :icon="['fas', 'clock']" class="mr-1" />
          {{ showSnoozed ? 'Showing Snoozed' : 'Show Snoozed' }}
        </button>
        <button
          @click="toggleShowExpired"
          :title="'Show patients who dropped off the payer\'s latest file (expired, history preserved). Work can still be logged against them.'"
          :class="[
            'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
            showExpired
              ? 'bg-gray-600 text-white border-gray-600'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
          ]"
        >
          <FontAwesomeIcon :icon="['fas', 'box-archive']" class="mr-1" />
          {{ showExpired ? 'Showing Expired' : 'Show Expired' }}
        </button>
        <button
          @click="toggleShowCompleted"
          :class="[
            'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
            showCompleted
              ? 'bg-green-600 text-white border-green-600'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
          ]"
        >
          <FontAwesomeIcon :icon="['fas', 'check-circle']" class="mr-1" />
          {{ showCompleted ? 'Showing Completed' : 'Show Completed' }}
        </button>
      </template>
    </Worklist_FilterBar>

    <!-- Toolbar -->
    <div class="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
      <div class="flex items-center space-x-4">
        <h2 class="text-lg font-semibold text-gray-900">Quality Gap Closure</h2>
        <span v-if="!showCampaignEmptyState" class="text-sm text-gray-500">
          <span v-if="countsLoading" class="inline-block h-4 w-24 align-middle bg-gray-200 rounded animate-pulse"></span>
          <template v-else>{{ (stats.totalPatients ?? 0).toLocaleString() }} patients</template>
        </span>
      </div>

      <div v-if="!showCampaignEmptyState" class="flex items-center space-x-4">
        <button
          v-if="hasAnyFilters || hasPatientSearch"
          @click="clearAllFilters"
          class="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700"
        >
          <FontAwesomeIcon :icon="['fas', 'times']" class="mr-1" />
          Clear All
        </button>
        <button
          @click="saveFilters"
          :disabled="!isFilterDirty"
          :class="[
            'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
            isFilterDirty
              ? 'border-primary-500 bg-primary-500 text-white hover:opacity-90'
              : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
          ]"
        >
          <FontAwesomeIcon :icon="['fas', 'floppy-disk']" class="text-[10px]" />
          Save Filters
        </button>
        <button
          @click="refreshGrid"
          :disabled="loading"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" class="text-[10px]" :class="{ 'animate-spin': loading }" />
          Refresh
        </button>
        <ExportMenu :exports="[
          { type: 'csv', label: 'Practice List', action: exportPracticeList, loading: exportingPracticeList },
        ]" />
        <div class="flex items-center text-xs text-gray-600">
          <span v-if="countsLoading" class="inline-block h-3 w-20 bg-gray-200 rounded animate-pulse"></span>
          <template v-else>
            <span class="font-medium text-orange-600">{{ (stats.openGaps ?? 0).toLocaleString() }}</span>
            <span class="ml-1">open gaps</span>
          </template>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="flex-1 flex overflow-hidden">
      <!-- Left: AG-Grid List -->
      <div
        :style="{ width: selectedPatient ? '50%' : '100%' }"
        class="transition-all duration-300 flex flex-col border-r border-gray-200"
        style="height: calc(100vh - 200px)"
      >
        <!-- Campaign selected but not yet generated -->
        <div
          v-if="showCampaignEmptyState"
          class="flex-1 flex flex-col items-center justify-center text-center p-10"
        >
          <FontAwesomeIcon :icon="['fas', 'bullseye']" class="text-4xl text-gray-300 mb-4" />
          <div class="text-lg font-semibold text-gray-900 mb-1 sensitive">{{ selectedCampaignName }}</div>
          <div class="text-sm text-gray-600 mb-5">
            <template v-if="campaignPendingLoading">Checking matching gaps…</template>
            <template v-else-if="campaignPendingCount > 0">
              <span class="font-semibold text-gray-900">{{ campaignPendingCount }}</span> opportunities ready to be generated.
            </template>
            <template v-else>No matching gaps to generate yet.</template>
          </div>
          <button
            v-if="campaignPendingCount > 0 && canManageCampaigns"
            @click="generateCampaignNow"
            :disabled="campaignGenerating"
            class="px-4 py-2 rounded-lg bg-blue-dark text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {{ campaignGenerating ? 'Generating…' : 'Generate now' }}
          </button>
          <div
            v-else-if="campaignPendingCount > 0"
            class="text-xs text-gray-500"
          >
            Ask a manager to generate this campaign.
          </div>
        </div>

        <!-- AG-Grid -->
        <AgGridVue
          v-else
          :key="tableKey"
          :theme="gridTheme"
          class="flex-1 w-full"
          :column-defs="computedColumnDefs"
          :default-col-def="defaultColDef"
          :grid-options="gridOptions"
          :loading="loading"
          :row-height="100"
          :header-height="48"
          :row-model-type="'serverSide'"
          :get-row-id="getRowId"
          :row-class-rules="rowClassRules"
          @grid-ready="onGridReady"
          @row-clicked="onRowClicked"
        />

        <!-- Footer -->
        <div v-if="!showCampaignEmptyState" class="px-4 py-2 border-t border-gray-200 bg-gray-50">
          <div class="flex items-center justify-between text-xs">
            <div class="flex items-center space-x-4 text-gray-600">
              <span v-if="countsLoading" class="inline-block h-3 w-40 bg-gray-200 rounded animate-pulse"></span>
              <span v-else>Showing {{ (displayedRowCount ?? 0).toLocaleString() }} of {{ (stats.totalPatients ?? 0).toLocaleString() }} patients</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Patient Drawer -->
      <transition
        enter-active-class="transition-all duration-300"
        leave-active-class="transition-all duration-300"
        enter-from-class="opacity-0 translate-x-4"
        leave-to-class="opacity-0 translate-x-4"
      >
        <div
          v-if="selectedPatient"
          class="flex flex-col overflow-hidden transition-all duration-300"
          style="width: 50%; height: calc(100vh - 200px)"
        >
          <Patient_Drawer
            ref="patientDrawer"
            :patient-identifier="selectedPatient.patient_identifier"
            :entity-id="selectedPatient.entity_id"
            :patient-data="selectedPatientData"
            :patient-name="selectedPatient.patient_name"
            :enabled-tabs="['details', 'utilization', 'gaps', 'engagement', 'care_team']"
            :default-tab="'gaps'"
            :include-expired-gaps="showExpired"
            :campaign-membership-id="drawerCampaignMembershipId"
            :show-header="true"
            @show-message="showMessage"
            @task-updated="softRefreshGrid"
            @close="closeDetail"
          />
        </div>
      </transition>
    </div>

    <!-- Success/Error Messages -->
    <transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="message"
        :class="[
          'fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-50 max-w-md',
          message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        ]"
      >
        <div class="flex items-start">
          <FontAwesomeIcon
            :icon="['fas', message.type === 'success' ? 'check-circle' : 'exclamation-circle']"
            class="mr-2 mt-0.5"
          />
          <div>
            <div class="font-medium">{{ message.text }}</div>
          </div>
        </div>
      </div>
    </transition>

    <Quality_Campaign_Modal
      :is-open="campaignModalOpen"
      @close="campaignModalOpen = false"
      @select="onCampaignSelected"
      @generated="onCampaignGenerated"
    />
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3';
import { themeQuartz } from 'ag-grid-enterprise';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { isDemoMode, spoofText } from '@/utils/demoSpoof';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import Worklist_FilterBar from '@/components/widgetcomponents/Worklist_FilterBar.vue';
import Quality_PatientCell from './cells/Quality_PatientCell.vue';
import Quality_CompactPatientCell from './cells/Quality_CompactPatientCell.vue';
import Quality_ProviderCell from './cells/Quality_ProviderCell.vue';
import Quality_EntityCell from './cells/Quality_EntityCell.vue';
import Quality_GapCountCell from './cells/Quality_GapCountCell.vue';
import Quality_ActionTypeCell from './cells/Quality_ActionTypeCell.vue';
import Quality_LastWorkCell from './cells/Quality_LastWorkCell.vue';
import Quality_DueDateCell from './cells/Quality_DueDateCell.vue';
import Quality_MeasureCell from './cells/Quality_MeasureCell.vue';
import Quality_PriorityCell from './cells/Quality_PriorityCell.vue';
import Quality_SkeletonOverlay from './Quality_SkeletonOverlay.vue';
import CC_Work_Summary_Column from '@/components/Portal/Task_Tables/Custom_Cell_Renderers/CC_Work_Summary_Column.vue';
import CC_Payer_Composite_Renderer from '@/components/Portal/Task_Tables/Custom_Cell_Renderers/CC_Payer_Composite_Renderer.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import Quality_Campaign_Modal from '@/components/Quality/Quality_Campaign_Modal.vue';
import { getFilterOptions, getPatientList } from '@/services/api/qualityPatientList';
import { mapGetters } from 'vuex';
import isEqual from 'lodash/isEqual';
import { z } from 'zod';
import { BrowserStore } from '@/utils/BrowserStore';

// Each person's own filter-bar choices (entity filters + My Practices/My
// Contracts), remembered in their browser when they click Save Filters —
// same as CareManagement_Patients_List.
const filterStore = new BrowserStore(
  'quality_patient_list_saved_filters',
  z.object({
    entityActiveFilters: z.array(z.object({
      id: z.number(),
      name: z.string(),
      typeName: z.string(),
    })).default([]),
    entitySelectedType: z.string().nullable().default(null),
    myPracticesActive: z.boolean().default(false),
    evidenceFilterActive: z.boolean().default(false),
    closureFilterActive: z.boolean().default(false),
    favoritesFilterActive: z.boolean().default(false),
    showExpired: z.boolean().default(false),
    showCompleted: z.boolean().default(false),
    selectedRoutingTier: z.string().nullable().default(null),
    selectedStage: z.number().nullable().default(null),
  })
);
// Filters this page saved on its own before this store existed.
const LEGACY_ENTITY_FILTER_KEY = 'quality_entity_filter';
const LEGACY_ROUTING_TIER_KEY = 'quality_routing_tier';
const LEGACY_STAGE_KEY = 'quality_stage';

function loadSavedFilters() {
  if (filterStore.hasSavedData()) return filterStore.load();
  try {
    const legacy = JSON.parse(localStorage.getItem(LEGACY_ENTITY_FILTER_KEY) || 'null');
    const tier = localStorage.getItem(LEGACY_ROUTING_TIER_KEY);
    const stage = localStorage.getItem(LEGACY_STAGE_KEY);
    if (legacy?.filters?.length || tier || stage) {
      const migrated = filterStore.schema.parse({
        entityActiveFilters: legacy?.filters || [],
        entitySelectedType: legacy?.type || null,
        selectedRoutingTier: tier || null,
        selectedStage: stage ? Number(stage) : null,
      });
      filterStore.save(migrated);
      [LEGACY_ENTITY_FILTER_KEY, LEGACY_ROUTING_TIER_KEY, LEGACY_STAGE_KEY].forEach(k => localStorage.removeItem(k));
      return migrated;
    }
  } catch {}
  return filterStore.load();
}


export default {
  name: 'Quality_Patients_List',
  components: {
    AgGridVue,
    FontAwesomeIcon,
    Patient_Drawer,
    Worklist_FilterBar,
    Quality_PatientCell,
    Quality_CompactPatientCell,
    Quality_ProviderCell,
    Quality_EntityCell,
    Quality_GapCountCell,
    Quality_ActionTypeCell,
    Quality_LastWorkCell,
    Quality_DueDateCell,
    Quality_MeasureCell,
    Quality_PriorityCell,
    CC_Work_Summary_Column,
    ExportMenu,
    Quality_Campaign_Modal,
  },
  data() {
    const savedFilters = loadSavedFilters();
    return {
      // Handed to Worklist_FilterBar once; it restores and emits from there.
      entityInitialFilters:
        savedFilters.entityActiveFilters.length > 0 || savedFilters.myPracticesActive
          ? {
              filters: savedFilters.entityActiveFilters,
              type: savedFilters.entitySelectedType,
              myPracticesActive: savedFilters.myPracticesActive,
            }
          : null,
      gridApi: null,
      loading: false,
      // True while the first page loads (page open, filter change, refresh):
      // the header counts show placeholders instead of 0 or stale totals.
      // Scrolling for more rows does not change the totals, so it doesn't blank them.
      countsLoading: true,
      tableKey: 0,
      selectedPatient: null,
      message: null,
      displayedRowCount: 0,
      exportingPracticeList: false,

      campaignModalOpen: false,
      selectedCampaignId: null,
      selectedCampaignName: null,
      selectedCampaignHasGenerated: false,
      campaignPendingCount: 0,
      campaignPendingLoading: false,
      campaignGenerating: false,

      // Entity filters from Worklist_FilterBar
      entityFilters: null,
      // Start from the saved choice so the first list request already has it.
      myPractices: savedFilters.myPracticesActive,
      // Current filter-bar state, compared with the saved snapshot for Save Filters.
      entityActiveFilters: savedFilters.entityActiveFilters,
      entitySelectedType: savedFilters.entitySelectedType,
      savedFilterSnapshot: filterStore.hasSavedData()
        ? JSON.parse(JSON.stringify(savedFilters))
        : null,
      // From quality filter options: what My Practices resolves to for this
      // user ('Practice' / 'Contract'), or null when it would not narrow.
      myPracticesEntityType: null,
      evidenceFilterActive: savedFilters.evidenceFilterActive,
      closureFilterActive: savedFilters.closureFilterActive,
      favoritesFilterActive: savedFilters.favoritesFilterActive,
      showExpired: savedFilters.showExpired,
      showSnoozed: false,
      showCompleted: savedFilters.showCompleted,
      selectedRoutingTier: savedFilters.selectedRoutingTier,
      routingDropdownOpen: false,
      routingTiers: [
        { value: 'chart_review', label: 'Chart Review' },
        { value: 'outreach', label: 'Patient Outreach' },
        { value: 'practice_list', label: 'Practice List' }
      ],
      selectedStage: savedFilters.selectedStage,
      stageDropdownOpen: false,
      stageOptions: [],
      searchLastName: undefined,
      searchFirstName: undefined,
      searchDob: undefined,
      searchMbi: undefined,

      // Cross-contract "patient list for selected measures" hand-off from the
      // Contract Performance tab (route query).
      measureCodes: null,
      payerNameFilter: null,
      contractNameFilter: null,

      defaultColDef: {
        sortable: true,
        resizable: true,
        filter: false
      },
      cellStyleTruncate: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      fullColumnDefs: [
        {
          headerName: '',
          field: 'is_favorited',
          width: 40,
          maxWidth: 40,
          cellClass: 'cell-theme-center-aligned',
          cellRenderer: params => {
            if (!params.data) return '';
            const isFav = params.data.is_favorited;
            return `<span class="favorite-star text-base ${isFav ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-400'}" style="cursor:pointer">★</span>`;
          },
        },
        {
          headerName: 'Payer',
          field: 'payer_name',
          cellRenderer: 'CC_Payer_Composite_Renderer',
          context: { demoSensitive: true },
          cellStyle: { overflow: 'hidden', textOverflow: 'ellipsis' },
          cellClass: 'cell-theme-left-aligned',
        },
        {
          headerName: 'Patient',
          field: 'patient_info',
          cellRenderer: 'Quality_PatientCell',
          context: { demoSensitive: true },
          cellStyle: { overflow: 'hidden' },
          cellClass: 'cell-theme-left-aligned',
        },
        {
          headerName: 'Practice',
          field: 'practice_name',
          cellRenderer: 'Quality_EntityCell',
          cellStyle: { overflow: 'hidden' },
          cellClass: 'cell-theme-left-aligned sensitive',
          tooltipValueGetter: params => {
            let p = params.data?.practice_name || '';
            let v = params.data?.provider_name || '';
            // ag-grid renders tooltips in a detached popup the DOM spoof engine
            // never sees, so spoof practice/provider names here in Demo Mode.
            if (isDemoMode()) {
              p = p ? spoofText(p) : p;
              v = v ? spoofText(v) : v;
            }
            return p && v ? `${p} / ${v}` : p || v;
          }
        },
        {
          headerName: 'Pri',
          field: 'priority',
          cellRenderer: 'Quality_PriorityCell',
          cellClass: 'cell-theme-center-aligned',
          width: 55,
        },
        {
          headerName: 'Gaps',
          field: 'measure_codes',
          cellRenderer: 'Quality_GapCountCell',
          // Dedicated class: overflow must stay visible so the
          // "potentially closed" checkmark on the last badge isn't clipped.
          cellClass: 'cell-theme-gap-badges',
          autoHeight: true,
          minWidth: 150,
        },
        {
          headerName: 'Evidence',
          field: 'has_clinical_evidence',
          cellClass: 'cell-theme-center-aligned',
          width: 70,
          cellRenderer: params => {
            if (!params.data?.has_clinical_evidence) return ''
            const parts = []
            const ehrCount = params.data.ehr_evidence_count || 0
            const claimsCount = params.data.claims_evidence_count || 0
            if (ehrCount > 0) {
              parts.push(`<span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700" title="${ehrCount} EHR evidence (could be closed)">EHR ${ehrCount}</span>`)
            }
            if (claimsCount > 0) {
              parts.push(`<span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700" title="${claimsCount} claims evidence (probably closed)">CLM ${claimsCount}</span>`)
            }
            return `<div class="flex flex-col gap-0.5 items-center">${parts.join('')}</div>`
          }
        },
        {
          // Current workflow stage of the patient's gap task. Empty for gap
          // metrics with no task chain — most care gaps today carry no task.
          headerName: 'Stage',
          field: 'task_stage',
          cellClass: 'cell-theme-left-aligned',
          minWidth: 140,
          cellRenderer: params => {
            const stage = params.data?.task_stage
            if (!stage) return ''
            const esc = s => String(s).replace(/[&<>"]/g, c => (
              { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
            ))
            const status = params.data.task_status || ''
            const due = params.data.task_due_date
            const snoozed = status === 'Snooze'
            const overdue = due && !snoozed && new Date(due + 'T23:59:59') < new Date()

            const parts = [
              `<span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700" title="${esc(stage)}">${esc(stage)}</span>`
            ]
            if (snoozed) {
              parts.push('<span class="text-[10px] text-yellow-700">Snoozed</span>')
            } else if (overdue) {
              parts.push('<span class="text-[10px] text-red-600 font-medium">Overdue</span>')
            } else if (due) {
              const d = new Date(due + 'T00:00:00')
              parts.push(`<span class="text-[10px] text-gray-500">Due ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>`)
            }
            return `<div class="flex flex-col gap-0.5 leading-tight">${parts.join('')}</div>`
          }
        },
        {
          headerName: 'Work',
          field: 'work_completed',
          cellRenderer: 'CC_Work_Summary_Column',
          cellClass: 'cell-theme-left-aligned',
          autoHeight: true,
        }
      ],
      gridOptions: {
        rowModelType: 'serverSide',
        suppressHorizontalScroll: false,
        animateRows: true,
        enableCellTextSelection: true,
        ensureDomOrder: true,
        suppressColumnVirtualisation: true,
        suppressSizeToFit: false,
        tooltipShowDelay: 300,
        // Skeleton rows instead of ag-grid's "Loading..." box.
        loadingOverlayComponent: 'Quality_SkeletonOverlay',
        components: {
          Quality_SkeletonOverlay,
          Quality_PatientCell,
          Quality_CompactPatientCell,
          Quality_ProviderCell,
          Quality_EntityCell,
          Quality_GapCountCell,
          Quality_ActionTypeCell,
          Quality_LastWorkCell,
          Quality_DueDateCell,
          Quality_MeasureCell,
          Quality_PriorityCell,
          CC_Work_Summary_Column,
          CC_Payer_Composite_Renderer
        },
        autoSizeStrategy: {
          type: 'fitCellContents'
        }
      },
      rowClassRules: {
        'bg-blue-50': params => params.data?.patient_identifier === this.selectedPatient?.patient_identifier
      },
      gridTheme: themeQuartz.withParams({
        browserColorScheme: 'light',
        backgroundColor: '#ffffff',
        foregroundColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderRadius: 0,
        fontFamily: 'inherit',
        fontSize: '14px',
        headerBackgroundColor: '#f9fafb',
        headerFontSize: '12px',
        headerFontWeight: 600,
        oddRowBackgroundColor: '#ffffff',
        rowBorder: true,
        rowHoverColor: '#f3f4f6',
        spacing: 8,
        wrapperBorder: false
      }),
      stats: {
        totalPatients: 0,
        openGaps: 0,
        overdueGaps: 0
      }
    };
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutsideRouting);
    document.addEventListener('click', this.handleClickOutsideStage);
    // Cross-contract measure hand-off (route): /quality/patients?measure_codes=COL-E[&payer_name=...]
    const q = this.$route?.query || {};
    if (q.measure_codes) {
      this.measureCodes = String(q.measure_codes).split(',').map(s => s.trim()).filter(Boolean);
    }
    if (q.payer_name) {
      this.payerNameFilter = String(q.payer_name);
    }
    // Contract-scoped hand-off from the Contract Performance Trackers tab:
    // /quality/patients?measure_codes=COL-E&contract_name=Anthem%20Medicaid...
    if (q.contract_name) {
      this.contractNameFilter = String(q.contract_name);
    }
    this.loadRoutingTierCounts();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutsideRouting);
    document.removeEventListener('click', this.handleClickOutsideStage);
  },
  computed: {
    ...mapGetters('tenant', ['hasRole', 'isManagerViewer']),
    ...mapGetters('auth', { currentUser: 'current_user' }),
    canManageCampaigns() {
      if (this.currentUser && this.currentUser.is_superuser) return true;
      return this.hasRole('admin') || this.isManagerViewer;
    },
    showCampaignEmptyState() {
      return !!this.selectedCampaignId && !this.selectedCampaignHasGenerated;
    },
    drawerCampaignMembershipId() {
      return (this.selectedCampaignId && this.selectedCampaignHasGenerated)
        ? this.selectedCampaignId : null;
    },
    routingTierLabel() {
      const tier = this.routingTiers.find(t => t.value === this.selectedRoutingTier);
      return tier ? tier.label : 'Next Step';
    },
    stageLabel() {
      const stage = this.stageOptions.find(s => s.id === this.selectedStage);
      return stage ? stage.name : 'Stage';
    },
    currentFilterState() {
      return {
        entityActiveFilters: this.entityActiveFilters,
        entitySelectedType: this.entitySelectedType,
        myPracticesActive: this.myPractices,
        evidenceFilterActive: this.evidenceFilterActive,
        closureFilterActive: this.closureFilterActive,
        favoritesFilterActive: this.favoritesFilterActive,
        showExpired: this.showExpired,
        showCompleted: this.showCompleted,
        selectedRoutingTier: this.selectedRoutingTier,
        selectedStage: this.selectedStage,
      };
    },
    hasAnyFilters() {
      return this.entityActiveFilters.length > 0 || this.myPractices ||
        this.evidenceFilterActive || this.closureFilterActive || this.favoritesFilterActive ||
        this.showExpired || this.showCompleted ||
        this.selectedRoutingTier !== null || this.selectedStage !== null;
    },
    hasPatientSearch() {
      return !!(this.searchLastName || this.searchFirstName || this.searchDob || this.searchMbi);
    },
    isFilterDirty() {
      if (this.savedFilterSnapshot === null) return this.hasAnyFilters;
      return !isEqual(this.currentFilterState, this.savedFilterSnapshot);
    },
    computedColumnDefs() {
      if (this.selectedPatient) return this.drawerColumnDefs;
      return this.fullColumnDefs;
    },
    drawerColumnDefs() {
      // When patient drawer is open, hide Work Required column
      return this.fullColumnDefs.filter(col => col.field !== 'work_status');
    },
    selectedPatientData() {
      if (!this.selectedPatient) return null;
      return {
        bene_first_name: this.selectedPatient.bene_first_name,
        bene_last_name: this.selectedPatient.bene_last_name,
        bene_dob: this.selectedPatient.bene_dob,
        bene_sex_cd: this.selectedPatient.bene_sex_cd,
        bene_age: this.selectedPatient.bene_age,
        bene_death_dt: this.selectedPatient.bene_death_dt,
        // Contact fields
        bene_phone: this.selectedPatient.bene_phone,
        bene_address_1: this.selectedPatient.bene_address_1,
        bene_address_2: this.selectedPatient.bene_address_2,
        bene_city: this.selectedPatient.bene_city,
        bene_state: this.selectedPatient.bene_state,
        bene_zip: this.selectedPatient.bene_zip,
        // Care context fields
        provider_name: this.selectedPatient.provider_name,
        practice_name: this.selectedPatient.practice_name,
        payer_name: this.selectedPatient.payer_name,
        contract_name: this.selectedPatient.contract_name,
        lob: this.selectedPatient.lob,
        language: this.selectedPatient.language,
        last_service_date: this.selectedPatient.last_service_date,
      };
    }
  },
  watch: {
    selectedPatient() {
      if (this.gridApi) {
        this.gridApi.setGridOption('columnDefs', this.computedColumnDefs);
        setTimeout(() => {
          this.gridApi.sizeColumnsToFit();
        }, 350);
      }
    }
  },
  methods: {
    getRowId(params) {
      return params.data.patient_identifier;
    },

    onFilterChanged(filterData) {
      this.entityFilters = filterData?.entity_filters || null;
      this.myPractices = filterData?.my_practices || false;
      const bar = this.$refs.entityFilterBar;
      if (bar) {
        this.entityActiveFilters = [...bar.activeFilters];
        this.entitySelectedType = bar.selectedType;
      }
      this.searchLastName = filterData?.search_last_name;
      this.searchFirstName = filterData?.search_first_name;
      this.searchDob = filterData?.search_dob;
      this.searchMbi = filterData?.search_mbi;
      this.refreshGrid();
    },

    getActiveFilters() {
      return {
        entity_filters: this.entityFilters || undefined,
        my_practices: this.myPractices || undefined,
        gap_status: this.showCompleted ? 'closed' : 'open',
        has_evidence_only: this.evidenceFilterActive || undefined,
        routing_tier: this.selectedRoutingTier || undefined,
        stages: this.selectedStage ? [this.selectedStage] : undefined,
        gap_closure_status: this.closureFilterActive ? 'potentially_closed' : undefined,
        favorites_only: this.favoritesFilterActive || undefined,
        include_expired: this.showExpired || undefined,
        show_snoozed: this.showSnoozed || undefined,
        search_last_name: this.searchLastName || undefined,
        search_first_name: this.searchFirstName || undefined,
        search_dob: this.searchDob || undefined,
        search_mbi: this.searchMbi || undefined,
        // Cross-contract measure hand-off (HEDIS codes).
        measure_codes: this.measureCodes && this.measureCodes.length ? this.measureCodes : undefined,
        payer_name: this.payerNameFilter || undefined,
        contract_name: this.contractNameFilter || undefined,
        campaign_membership_id: (this.selectedCampaignId && this.selectedCampaignHasGenerated)
          ? this.selectedCampaignId : undefined,
      };
    },
    onCampaignSelected(campaign) {
      this.selectedCampaignId = campaign?.id || null;
      this.selectedCampaignName = campaign?.name || null;
      this.selectedCampaignHasGenerated = (campaign?.generated_count || 0) > 0;
      this.campaignModalOpen = false;
      this.campaignPendingCount = 0;
      if (this.selectedCampaignId && !this.selectedCampaignHasGenerated) {
        this.fetchCampaignPending();
      } else {
        this.tableKey += 1;
      }
    },
    async fetchCampaignPending() {
      const id = this.selectedCampaignId;
      this.campaignPendingLoading = true;
      try {
        const { data } = await this.$axios.post(
          `/api/opportunity-campaigns/${id}/generate/`,
          { dry_run: true },
        );
        if (this.selectedCampaignId === id) {
          this.campaignPendingCount = data?.to_create || 0;
        }
      } catch (e) {
        console.error('Campaign preview failed', e);
        if (this.selectedCampaignId === id) this.campaignPendingCount = 0;
      } finally {
        this.campaignPendingLoading = false;
      }
    },
    async generateCampaignNow() {
      if (!this.canManageCampaigns) return;
      const id = this.selectedCampaignId;
      this.campaignGenerating = true;
      try {
        await this.$axios.post(
          `/api/opportunity-campaigns/${id}/generate/`,
          { dry_run: false },
        );
        if (this.selectedCampaignId === id) {
          this.selectedCampaignHasGenerated = true;
          this.tableKey += 1;
        }
      } catch (e) {
        console.error('Campaign generation failed', e);
      } finally {
        this.campaignGenerating = false;
      }
    },
    onCampaignGenerated(campaign) {
      if (campaign && campaign.id === this.selectedCampaignId) {
        this.selectedCampaignHasGenerated = true;
        this.tableKey += 1;
      }
    },
    clearCampaign() {
      this.selectedCampaignId = null;
      this.selectedCampaignName = null;
      this.selectedCampaignHasGenerated = false;
      this.campaignPendingCount = 0;
      this.tableKey += 1;
    },

    selectRoutingTier(tier) {
      this.selectedRoutingTier = tier;
      this.routingDropdownOpen = false;
      this.refreshGrid();
    },

    handleClickOutsideRouting(event) {
      if (this.$refs.routingDropdown && !this.$refs.routingDropdown.contains(event.target)) {
        this.routingDropdownOpen = false;
      }
    },

    selectStage(stageId) {
      this.selectedStage = stageId;
      this.stageDropdownOpen = false;
      this.refreshGrid();
    },

    handleClickOutsideStage(event) {
      if (this.$refs.stageDropdown && !this.$refs.stageDropdown.contains(event.target)) {
        this.stageDropdownOpen = false;
      }
    },

    async loadRoutingTierCounts() {
      try {
        const data = await getFilterOptions();
        if (data?.routing_tiers) {
          this.routingTiers = data.routing_tiers;
        }
        if (data?.stages) {
          this.stageOptions = data.stages;
        }
        this.myPracticesEntityType = data?.my_practices_entity_type ?? null;
      } catch (e) {
        // Counts are optional — filter still works without them
      }
    },

    // Clears every filter on the page. The filter bar's clearAll() emits
    // filter-changed, which reloads the list once. Not saved until Save Filters.
    clearAllFilters() {
      this.evidenceFilterActive = false;
      this.closureFilterActive = false;
      this.favoritesFilterActive = false;
      this.showExpired = false;
      this.showCompleted = false;
      this.selectedRoutingTier = null;
      this.selectedStage = null;
      if (this.$refs.entityFilterBar) {
        this.$refs.entityFilterBar.clearAll();
      } else {
        this.refreshGrid();
      }
    },

    saveFilters() {
      const state = this.currentFilterState;
      filterStore.save(state);
      this.savedFilterSnapshot = JSON.parse(JSON.stringify(state));
    },

    toggleEvidenceFilter() {
      this.evidenceFilterActive = !this.evidenceFilterActive;
      this.refreshGrid();
    },

    toggleClosureFilter() {
      this.closureFilterActive = !this.closureFilterActive;
      this.refreshGrid();
    },

    toggleFavoritesFilter() {
      this.favoritesFilterActive = !this.favoritesFilterActive;
      this.refreshGrid();
    },

    toggleShowSnoozed() {
      this.showSnoozed = !this.showSnoozed;
      this.refreshGrid();
    },

    toggleShowExpired() {
      this.showExpired = !this.showExpired;
      this.refreshGrid();
    },

    toggleShowCompleted() {
      this.showCompleted = !this.showCompleted;
      this.refreshGrid();
    },

    async toggleFavorite(rowData) {
      const patientId = rowData.patient_identifier;
      try {
        const { data } = await this.$axios.post('/api/quality/toggle-favorite/', {
          patient_identifier: patientId,
          opportunity_id: rowData.first_opportunity_id,
        });
        rowData.is_favorited = data.favorited;
        // Refresh just this row
        const rowNode = this.gridApi.getRowNode(patientId);
        if (rowNode) {
          rowNode.setData({ ...rowData });
        }
      } catch (e) {
        console.error('Failed to toggle favorite', e);
      }
    },

    async onGridReady(params) {
      this.gridApi = params.api;
      this.gridApi.setGridOption('context', { componentParent: this });

      const datasource = {
        getRows: async (paramsRequest) => {
          // AG-Grid's server-side row model ties paramsRequest.success() to THIS
          // request, so stale rows can't overwrite the grid. But `this.stats` and
          // `this.displayedRowCount` are assigned to component state directly and
          // CAN go stale: 15% of quality patient-list calls overlap an in-flight
          // one (645 of 4,281 over 7 days), so a slow earlier reply can land last
          // and leave the header counts describing the previous filter set.
          const seq = (this._gridSeq = (this._gridSeq || 0) + 1);
          try {
            this.loading = true;
            if (paramsRequest.request.startRow === 0) this.countsLoading = true;

            const data = await getPatientList({
              ...this.getActiveFilters(),
              start_row: paramsRequest.request.startRow,
              end_row: paramsRequest.request.endRow,
              sort_model: paramsRequest.request.sortModel?.length ? paramsRequest.request.sortModel : undefined,
            });

            if (data?.success) {
              // Component-state writes are guarded; the grid callback below is
              // not, because AG-Grid needs every request answered exactly once.
              if (seq === this._gridSeq) {
                this.stats = data.stats || {
                  totalPatients: data.lastRow,
                  openGaps: 0,
                  overdueGaps: 0
                };
                this.displayedRowCount = data.rowData?.length || 0;
              }

              paramsRequest.success({
                rowData: data.rowData || [],
                lastRow: data.lastRow || 0
              });
            } else {
              paramsRequest.success({ rowData: [], lastRow: 0 });
            }

            setTimeout(() => {
              this.gridApi.sizeColumnsToFit();
            }, 100);
          } catch (error) {
            console.error('Error fetching quality patients:', error);
            paramsRequest.fail();
          } finally {
            if (seq === this._gridSeq) {
              this.loading = false;
              this.countsLoading = false;
            }
          }
        }
      };

      this.gridApi.setGridOption('serverSideDatasource', datasource);

      window.addEventListener('resize', () => {
        this.gridApi.sizeColumnsToFit();
      });
    },

    refreshGrid() {
      if (this.gridApi) {
        this.gridApi.refreshServerSide({ purge: true });
      }
    },

    softRefreshGrid() {
      if (this.gridApi) {
        this.gridApi.refreshServerSide({ purge: false });
      }
    },

    onRowClicked(event) {
      // Intercept star clicks — toggle favorite instead of opening drawer
      if (event.event?.target?.closest('.favorite-star') || event.column?.colId === 'is_favorited') {
        this.toggleFavorite(event.data);
        return;
      }
      this.selectedPatient = event.data;
      if (this.gridApi) {
        this.gridApi.redrawRows();
      }
    },

    closeDetail() {
      this.selectedPatient = null;
      if (this.gridApi) {
        this.gridApi.redrawRows();
        this.gridApi.setGridOption('columnDefs', this.computedColumnDefs);
        setTimeout(() => {
          this.gridApi.sizeColumnsToFit();
        }, 350);
      }
    },

    showMessage(text, type) {
      this.message = { text, type };
      setTimeout(() => {
        this.message = null;
      }, 5000);
    },

    async exportPracticeList() {
      this.exportingPracticeList = true;
      try {
        const response = await this.$axios.post(
          '/api/quality/export-practice-list/',
          {
            ...this.getActiveFilters(),
            format: 'csv'
          },
          { responseType: 'blob' }
        );

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `practice_list_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exporting practice list:', error);
        if (error.response?.status === 404) {
          alert('No gaps found for external practices with current filters.');
        } else {
          alert('Error exporting practice list. Please try again.');
        }
      } finally {
        this.exportingPracticeList = false;
      }
    },

    exportData() {
      this.gridApi.exportDataAsCsv({
        fileName: `quality_patients_${new Date().toISOString().split('T')[0]}.csv`
      });
    }
  }
};
</script>

<style scoped>
/* Custom styles if needed */
</style>
